import React, { useEffect, useState } from 'react';

export function AISilhouette({ src, alt, style = {}, fallback = null }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error && fallback) return fallback;

  return (
    <div style={{ position: 'relative', overflow: 'hidden', ...style }}>
      {!loaded && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, #1a0e04 0%, #0a0608 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            width: 40, height: 40,
            border: '2px solid #d4a44a30',
            borderTop: '2px solid #d4a44a',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.8s ease',
          filter: 'contrast(1.1) saturate(0.9)',
        }}
      />
      {loaded && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, transparent 40%, rgba(10,6,8,0.7) 100%)',
          pointerEvents: 'none',
        }} />
      )}
    </div>
  );
}

export function AICharacterCard({ character, imageUrl, description, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '3/4',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        overflow: 'hidden',
        background: '#0a0608',
        transition: 'transform 0.4s ease, box-shadow 0.4s ease',
        transform: hovered ? 'scale(1.02) translateY(-4px)' : 'scale(1)',
        boxShadow: hovered
          ? '0 20px 50px rgba(0,0,0,0.6), 0 0 30px rgba(212,164,74,0.15)'
          : '0 10px 30px rgba(0,0,0,0.4)',
      }}
    >
      <AISilhouette
        src={imageUrl}
        alt={character.name}
        style={{ position: 'absolute', inset: 0 }}
      />
      <div style={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        padding: '40px 20px 20px',
        background: 'linear-gradient(0deg, rgba(10,6,8,0.95) 0%, transparent 100%)',
        textAlign: 'left',
      }}>
        <div style={{
          fontFamily: 'var(--display)',
          fontSize: '14px',
          letterSpacing: '0.3em',
          color: '#d4a44a',
          textTransform: 'uppercase',
          marginBottom: '4px',
        }}>
          {character.yuga}
        </div>
        <div style={{
          fontFamily: 'var(--display)',
          fontStyle: 'italic',
          fontSize: '20px',
          color: '#e8dcc4',
          marginBottom: '8px',
        }}>
          {character.name}
        </div>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: '10px',
          color: '#a89070',
          letterSpacing: '0.1em',
        }}>
          {description}
        </div>
      </div>
      {hovered && (
        <div style={{
          position: 'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '12px 24px',
          border: '1px solid #d4a44a',
          background: 'rgba(10,6,8,0.8)',
          fontFamily: 'var(--mono)',
          fontSize: '10px',
          letterSpacing: '0.3em',
          color: '#d4a44a',
          textTransform: 'uppercase',
        }}>
          View Scene
        </div>
      )}
    </button>
  );
}

export function ManuAI() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 18,
        y: (e.clientY / window.innerHeight - 0.5) * 18,
      });
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
    }}>
      <img
        src="/assets/silhouettes/manu.png"
        alt="Manu"
        style={{
          width: '110%',
          height: '110%',
          objectFit: 'cover',
          objectPosition: 'center',
          filter: 'brightness(0.7) contrast(1.1)',
          transform: `translate(calc(-5% + ${mousePos.x}px), calc(-5% + ${mousePos.y}px))`,
          transition: 'transform 0.2s cubic-bezier(0.1, 0.4, 0.3, 1)',
        }}
      />

      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at 50% 50%, transparent 18%, rgba(26, 17, 7, 0.55) 100%)',
      }} />

      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '40%',
        background: 'linear-gradient(to bottom, transparent, var(--satya-bg))',
      }} />

      <div style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '15%',
        background: 'linear-gradient(to right, var(--satya-bg), transparent)',
      }} />
      <div style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        width: '15%',
        background: 'linear-gradient(to left, var(--satya-bg), transparent)',
      }} />
    </div>
  );
}

export function RamaAI() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none'
    }}>
      {/* Cinematic Image Integration with Parallax */}
      <img
        src="/assets/silhouettes/rama.png"
        alt="Divine Rama"
        style={{
          width: '110%',
          height: '110%',
          objectFit: 'cover',
          objectPosition: 'center',
          filter: 'brightness(0.7) contrast(1.1)',
          transform: `translate(calc(-5% + ${mousePos.x}px), calc(-5% + ${mousePos.y}px))`,
          transition: 'transform 0.2s cubic-bezier(0.1, 0.4, 0.3, 1)',
        }}
      />
      
      {/* Vignette and Blending Overlays */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at 50% 50%, transparent 20%, rgba(10, 6, 8, 0.4) 100%)',
      }} />
      
      {/* Bottom Fade to blend with page content */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '40%',
        background: 'linear-gradient(to bottom, transparent, #0a0608)',
      }} />

      {/* Side Fades for cinematic focus */}
      <div style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '15%',
        background: 'linear-gradient(to right, #0a0608, transparent)',
      }} />
      <div style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        width: '15%',
        background: 'linear-gradient(to left, #0a0608, transparent)',
      }} />
    </div>
  );
}

export function KrishnaAI() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * -20, // Reverse direction for different feel
        y: (e.clientY / window.innerHeight - 0.5) * -20
      });
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none'
    }}>
      {/* Cinematic Image Integration with Parallax */}
      <img
        src="/assets/silhouettes/krishna.png"
        alt="Divine Krishna"
        style={{
          width: '110%',
          height: '110%',
          objectFit: 'cover',
          objectPosition: 'center',
          filter: 'brightness(0.7) contrast(1.1)',
          transform: `translate(calc(-5% + ${mousePos.x}px), calc(-5% + ${mousePos.y}px))`,
          transition: 'transform 0.2s cubic-bezier(0.1, 0.4, 0.3, 1)',
        }}
      />
      
      {/* Vignette and Blending Overlays */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at 50% 50%, transparent 20%, rgba(5, 7, 20, 0.4) 100%)',
      }} />
      
      {/* Bottom Fade to blend with page content */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '40%',
        background: 'linear-gradient(to bottom, transparent, #050714)',
      }} />

      {/* Side Fades for cinematic focus */}
      <div style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '15%',
        background: 'linear-gradient(to right, #050714, transparent)',
      }} />
      <div style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        width: '15%',
        background: 'linear-gradient(to left, #050714, transparent)',
      }} />
    </div>
  );
}

export const AI_CHARACTERS = {
  manu: {
    name: 'Manu',
    yuga: 'Satya',
    prompt: 'Ancient Indian sage Manu standing in flowing white dhoti, long beard, serene expression, dramatic side lighting creating silhouette, cosmic golden background with mandala patterns, mythological painting style, cinematic lighting, 4K',
    scene: 'The flood waters rise. Manu builds his boat.',
  },
  rama: {
    name: 'Rama',
    yuga: 'Treta',
    prompt: 'Lord Rama as heroic archer, royal crown with tall mukut, holding massive bow, heroic standing pose, orange dhoti, bare chest, determined expression, dramatic golden hour lighting, Indian epic painting style, cinematic, 4K',
    scene: 'Rama strings his bow. The war for Lanka begins.',
  },
  hanuman: {
    name: 'Hanuman',
    yuga: 'Treta',
    prompt: 'Hanuman flying through clouds with mountain in hand, muscular heroic pose, flowing tail, fierce expression, golden armor, open mouth showing determination, dramatic clouds background, Indian epic painting style, cinematic lighting, 4K',
    scene: 'Hanuman leaps. The mountain blooms in his hand.',
  },
  krishna: {
    name: 'Krishna',
    yuga: 'Dvapara',
    prompt: 'Krishna playing flute under tree, blue skin, peacock feather crown, yellow dhoti, serene smiling expression, divine glow, cosmic background with stars, Indian miniature painting meets cinematic, golden lighting, 4K',
    scene: 'The flute plays. The cowherds dream.',
  },
  kalki: {
    name: 'Kalki',
    yuga: 'Kali',
    prompt: 'Kalki warrior on white horse with flaming sword raised to sky, dark stormy background with lightning, golden divine armor, fierce determination, cosmic destruction and rebirth theme, epic cinematic painting, 4K',
    scene: 'Lightning splits the sky. The sword descends.',
  },
  arjuna: {
    name: 'Arjuna',
    yuga: 'Dvapara',
    prompt: 'Arjuna at bow on chariot with Krishna as charioteer, massive bow drawn, divine halo, Krishna with steering reins, white horses, Kurukshetra battlefield dust and smoke, epic Indian mythological painting, cinematic, 4K',
    scene: 'The bow sings. Dharma hangs in balance.',
  },
  bhima: {
    name: 'Bhima',
    yuga: 'Dvapara',
    prompt: 'Bhima wielding massive iron mace, enormous muscular build, fierce warrior expression, ready to strike, battlefield background with smoke, dramatic lighting, Indian epic painting style, cinematic, 4K',
    scene: 'The mace rises. No armor will hold.',
  },
  draupadi: {
    name: 'Draupadi',
    yuga: 'Dvapara',
    prompt: 'Draupadi in royal silk saree with divine halo, saree billowing dramatically, fire in eyes, cosmic background with chakra symbols, empowered queen depiction, Indian mythological painting, cinematic golden lighting, 4K',
    scene: 'Her saree extends. Miracles unfold.',
  },
};

export function CharacterGallery({ onSelectCharacter }) {
  const characterList = Object.entries(AI_CHARACTERS).map(([key, data]) => ({
    key,
    ...data,
  }));

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '24px',
      padding: '40px 0',
    }}>
      {characterList.map(char => (
        <CharacterCard
          key={char.key}
          character={char}
          onClick={() => onSelectCharacter(char.key)}
        />
      ))}
    </div>
  );
}

function CharacterCard({ character, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '3/4',
        border: '1px solid #d4a44a20',
        padding: 0,
        cursor: 'pointer',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #1a0e04 0%, #0a0608 100%)',
        transition: 'all 0.4s ease',
        transform: hovered ? 'scale(1.02) translateY(-4px)' : 'scale(1)',
        boxShadow: hovered
          ? '0 20px 50px rgba(0,0,0,0.6), inset 0 0 40px rgba(212,164,74,0.1)'
          : '0 10px 30px rgba(0,0,0,0.4)',
      }}
    >
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
      }}>
        <div style={{
          fontFamily: 'var(--deva)',
          fontSize: '80px',
          color: '#d4a44a',
          opacity: 0.3,
          marginBottom: '20px',
        }}>
          {character.key === 'manu' ? 'ॐ' :
           character.key === 'rama' ? 'रा' :
           character.key === 'hanuman' ? 'ह' :
           character.key === 'krishna' ? 'कृ' :
           character.key === 'kalki' ? 'क' :
           character.key === 'arjuna' ? 'अ' :
           character.key === 'bhima' ? 'भी' : 'द'}
        </div>
        <div style={{
          fontFamily: 'var(--display)',
          fontSize: '11px',
          letterSpacing: '0.4em',
          color: '#d4a44a',
          textTransform: 'uppercase',
          marginBottom: '8px',
        }}>
          {character.yuga} Yuga
        </div>
        <div style={{
          fontFamily: 'var(--display)',
          fontStyle: 'italic',
          fontSize: '24px',
          color: '#e8dcc4',
          marginBottom: '12px',
        }}>
          {character.name}
        </div>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: '10px',
          color: '#a89070',
          textAlign: 'center',
          lineHeight: 1.6,
        }}>
          {character.scene}
        </div>
      </div>

      {hovered && (
        <div style={{
          position: 'absolute',
          bottom: 0, left: 0, right: 0,
          padding: '16px',
          background: 'linear-gradient(0deg, rgba(212,164,74,0.2) 0%, transparent 100%)',
          borderTop: '1px solid #d4a44a40',
        }}>
          <div style={{
            fontFamily: 'var(--mono)',
            fontSize: '9px',
            letterSpacing: '0.3em',
            color: '#d4a44a',
            textTransform: 'uppercase',
            textAlign: 'center',
          }}>
            Generate AI Image →
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
}
